import React, { useEffect, useRef, useState } from 'react';
import { userService } from 'services/user.service';
import { Paginator } from 'primereact/paginator';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { routes } from 'utils/routes';
import { useNavigate } from 'react-router-dom';
import DeleteItem from 'views/components/delete-modal';
import { SEOHeader } from 'views/components';
import { formatDate, formatPhoneNumber, getSortIcon, handleFilters } from 'utils/common-functions';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { mediaService } from 'services/media.service';
import { showTostify } from 'utils/common-functions';
import CustomToast from 'views/components/form-inputs/alert';
import { Skeleton } from 'primereact/skeleton';
import { _Object } from 'utils/interfaces';
import { useFormik } from 'formik';

const EmployeeListing = () => {
  const toastRef = useRef<any>(null);
  const [employeeList, setEmployeeList] = useState<any>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState<_Object>({})
  const [searchQuery] = useState('');
  const [visible, setVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const navigate = useNavigate();
  const [filterData, setFilterData] = useState<_Object>({
    page: 1,
    per_page: 10,
    filter: {
      role: "employee"
    },
    jobType: '', status: '',
    sort: '-created_at',
  })
  const ids: any = employeeList.filter((item: any) => item.id);

  const fetchEmployeeList = async () => {
    setLoading(true);
    try {
      const data = await userService.getUsers(filterData);
      // const data = await userService.getUsers({
      //   'filters[role]': 'employee',
      //   'filters[status]': filters.status || "",
      //   'filters[job_type]': filters.jobType || "",
      //   page: page + 1,
      //   per_page: perPage,
      //   sort,
      // });
      const avatarData = await mediaService.getMedia({
        'filters[entity_id]': ids,
      });

      const avatars = avatarData.items.reduce((acc: any, item: any) => {
        acc[item.entity_id] = item.url;
        return acc;
      }, {});

      const formattedData = data.items.map((item: any, index: number) => ({
        key: item.id,
        serialNumber: index + 1,
        employeeId: item.id_number || 'EN-1990',
        name: item.full_name,
        username: item.username,
        employType:
          item.job_type === "full_time" ? "Full Time" :
            item.job_type === "part_time" ? "Part Time" :
              item.job_type === "student" ? "Student" : "",
        email: item.email,
        joiningDate: new Date(item.created_at),
        joiningDateString: formatDate(item.created_at),
        phoneNumber: formatPhoneNumber(item.phone_number1),
        status: item.status === 'active' ? "Active" : "Inactive",
        avatar: avatars[item.id] || '',
      }));

      setEmployeeList(formattedData);
      setPagination(data.pagination);
    } catch (error) {
      console.error('Error fetching employees:', error);
    } finally {
      setLoading(false);
    }
  };

  const deleteItem = async () => {
    if (!itemToDelete) return;
    try {
      setLoading(true);
      const response = await userService.deleteUsers(itemToDelete);
      if (!response.error) {
        setVisible(false);
        await fetchEmployeeList();
        showTostify(toastRef, 'success', response.message);
        setItemToDelete(null);
      }
    } catch (error) {
      console.error('Error deleting employee:', error);
    } finally {
      setLoading(false);
    }
  };

  const onPageChange = (event: any) => {
    const currentPage = Math.floor(event.first / event.rows) + 1;

    setFilterData((prev) => ({
      ...prev,
      page: currentPage,
      per_page: event.rows,
    }));
  };

  useEffect(() => {
    fetchEmployeeList();
  }, [filterData]);

  const filteredEmployeeList = employeeList?.filter((employee: any) => {
    const lowercasedQuery = searchQuery?.toLowerCase();
    return (
      employee?.name?.toLowerCase()?.includes(lowercasedQuery) ||
      employee?.email?.toLowerCase()?.includes(lowercasedQuery) ||
      employee?.phoneNumber?.includes(lowercasedQuery)
    ) && employee?.employType?.toLowerCase()?.startsWith(filterData?.jobType?.toLowerCase()) &&
      (!filterData.status || employee.status === filterData.status);
  });

  const formikFilter = useFormik({
    initialValues: filterData,
    enableReinitialize: true,
    onSubmit: (values: _Object) => {
      setFilterData((prev) => ({
        ...prev,
        q: values.q.trim(),
        page: 1
      }));
    }
  });

  return (
    <>
      <SEOHeader title="Employees" />

      <CustomToast ref={toastRef} />
      <div className="employeelisting">
        <div className="flex justify-content-between align-items-center">
          <div className="p-inputgroup search-bar col-6">
            <form onSubmit={formikFilter.handleSubmit}>
              <div className='form-group mb-0 mr-2'>
                <Button type='submit' className='search-loading bg-transparent p-0 border-none'
                  disabled={loading}>
                  {loading && <i className="pi pi-spin pi-spinner"
                    style={{ fontSize: '1rem' }}
                  >
                  </i>}
                </Button>

                <IconField iconPosition="left">
                  <InputIcon className="pi pi-search"> </InputIcon>
                  <InputText placeholder="Search"
                    name='q'
                    onChange={formikFilter.handleChange}
                    onBlur={formikFilter.handleBlur}
                    value={formikFilter.values.q} />
                </IconField>

                {filterData.q && <Button type='button'
                  onClick={() => {
                    setFilterData((prev) => ({
                      ...prev,
                      q: '',
                      page: 1
                    }))
                  }}
                  severity="secondary" text className='bg-transparent p-0 border-none absolute top-0 right-0 mr-3'>
                  <span className="pi pi-times "> </span>
                </Button>}
              </div>
            </form>

            {pagination?.total_items > 0 &&
              <div className='total-items flex align-items-center text-sm'>
                Total items: {pagination.total_items || ""}
              </div>
            }
          </div>

          <div className="col-4 text-right">
            <Button label="Add New Employee" icon="pi pi-plus" className="primary" onClick={() => navigate(`${routes.employees}/create`)} />
          </div>
        </div>

        <div className="col">
          <div className="card w-full">
            <table className='w-full'>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>Username</th>
                  <th>
                    <button
                      className="btn p-0 border-0"
                      onClick={() => { handleFilters('sort', filterData.sort === '-employeeId' ? 'employeeId' : '-employeeId', setFilterData) }}>
                      EMP Id
                      <i className={`pi ${getSortIcon(filterData.sort.includes('employeeId') ? filterData.sort : '')}`}></i>
                    </button>
                  </th>
                  <th>EMP Type</th>
                  <th>
                    <button
                      className="btn p-0 border-0"
                      onClick={() => { handleFilters('sort', filterData.sort === '-created_at' ? 'created_at' : '-created_at', setFilterData) }}>
                      Joining Date
                      <i className={`pi ${getSortIcon(filterData.sort.includes('created_at') ? filterData.sort : '')}`}></i>
                    </button>
                  </th>
                  <th>Phone Number</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {loading && Array.from({ length: 5 }).map((_, index) => (
                  <tr key={index}>
                    <td><Skeleton height="2rem" borderRadius="1px" /></td>
                    <td><Skeleton height="2rem" borderRadius="4px" /></td>
                    <td><Skeleton height="2rem" borderRadius="4px" /></td>
                    <td><Skeleton height="2rem" borderRadius="4px" /></td>
                    <td><Skeleton height="2rem" borderRadius="4px" /></td>
                    <td><Skeleton height="2rem" borderRadius="4px" /></td>
                    <td><Skeleton height="2rem" borderRadius="4px" /></td>
                    <td><Skeleton height="2rem" borderRadius="4px" /></td>
                    <td><Skeleton height="2rem" borderRadius="4px" /></td>
                  </tr>
                ))}
                {!loading && filteredEmployeeList?.map((item: _Object, i: number) => (
                  <tr>
                    <td>{i + 1}</td>
                    <td> <div className="flex align-items-center gap-2">
                      <img alt={item.name} src={item?.avatar || `https://picsum.photos/200/300`} width="32" height="32" className="border-circle" />
                      <div>
                        <h5 className="m-0">{item?.name}</h5>
                        <p className="m-0">{item?.email}</p>
                      </div>
                    </div></td>
                    <td>{item.username || '-'}</td>
                    <td>{item.employeeId || '-'}</td>
                    <td>{item.employType || '-'}</td>
                    <td>{formatDate(item.joiningDate) || '-'}</td>
                    <td>{item.phoneNumber || '-'}</td>
                    <td><Tag value={item.status} severity={item.status === 'Active' ? 'success' : 'danger'} /></td>
                    <td>
                      <div>
                        <Button
                          icon="pi pi-pencil"
                          rounded
                          text
                          className="h-auto w-auto bg-transparent p-0 border-noround inline"
                          onClick={() => navigate(`${routes.employees}/edit/${item.key}`)}
                          style={{ color: '#959CB6' }}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
                {!loading && employeeList?.length === 0 && <tr>
                  <td colSpan={11}>No available options</td>
                </tr>
                }
              </tbody>
            </table>
          </div>

          {!loading && pagination.total_pages > 1 && (
            <Paginator
              first={(filterData.page - 1) * filterData.per_page}
              rows={filterData.per_page}
              totalRecords={pagination.total_items}
              rowsPerPageOptions={[10, 20, 30]}
              onPageChange={onPageChange}
            />
          )}
        </div>
      </div>

      <DeleteItem
        title="Employee"
        visible={visible}
        onHide={() => setVisible(false)}
        deleteItem={deleteItem}
      />
    </>
  );
};

export default EmployeeListing;
